import { environment } from '../../../environments/environment';
import {
  IAppRuntimeConfig,
  IFilterListItem,
  INameValueOption,
  IRuntimeConfigSection
} from '../shared/interfaces/common.interface';

export const BASE_SEARCH_PAGE_SESSION_KEY = 'base_search_page';

export const DEV_ENVIRONMENT_CONFIG = {
  googleAuth: {
    CLIENT_ID: environment.googleAuth.CLIENT_ID,
    API_KEY: environment.googleAuth.API_KEY
  },
  clarity: {
    projectId: ''
  },
  mapbox: {
    accessToken: environment.mapbox.accessToken,
    style: environment.mapbox.style,
    style1: 'mapbox://styles/mapbox/streets-v11'
  },
  loading: true,
  login_encrypt: false,
  login_type: 'auth0',
  intercom: {
    appId: environment.intercom.appId,
    secretKey: environment.intercom.secretKey
  },
  auth0: {
    domain: environment.auth0.domain,
    clientId: environment.auth0.clientId
  }
};

const RUNTIME_CONFIG_STORAGE_KEY = 'eml_avaana_runtime_config';

function isRecord(value: unknown): value is IRuntimeConfigSection {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function normalizeRuntimeConfig(configData: unknown): IAppRuntimeConfig {
  const dataField = isRecord(configData) ? configData['data'] : undefined;
  const source = isRecord(dataField) ? dataField : configData;
  if (!isRecord(source)) {
    return {};
  }

  return {
    googleAuth: isRecord(source['googleAuth']) ? source['googleAuth'] : undefined,
    mapbox: isRecord(source['mapbox']) ? source['mapbox'] : undefined,
    intercom: isRecord(source['intercom']) ? source['intercom'] : undefined,
    auth0: isRecord(source['intercom']) ? source['intercom'] : undefined
  };
}

function mergeRuntimeConfig(current: IAppRuntimeConfig, next: IAppRuntimeConfig): IAppRuntimeConfig {
  return {
    ...current,
    ...next,
    googleAuth: { ...current.googleAuth, ...next.googleAuth },
    mapbox: { ...current.mapbox, ...next.mapbox },
    intercom: { ...current.intercom, ...next.intercom },
    auth0: { ...current.auth0, ...next.auth0 }
  };
}

function applyRuntimeConfig(config: IAppRuntimeConfig): void {
  if (config.googleAuth) {
    Object.assign(DEV_ENVIRONMENT_CONFIG.googleAuth, config.googleAuth);
  }
  if (config.mapbox) {
    Object.assign(DEV_ENVIRONMENT_CONFIG.mapbox, config.mapbox);
  }
  if (config.intercom) {
    Object.assign(DEV_ENVIRONMENT_CONFIG.intercom, config.intercom);
  }
  if (config.auth0) {
    Object.assign(DEV_ENVIRONMENT_CONFIG.auth0, config.auth0);
  }
}

function readStoredRuntimeConfig(): IAppRuntimeConfig {
  try {
    const storedConfig = globalThis.localStorage.getItem(RUNTIME_CONFIG_STORAGE_KEY);
    if (!storedConfig) {
      return {};
    }

    return normalizeRuntimeConfig(JSON.parse(storedConfig));
  } catch {
    return {};
  }
}

function persistRuntimeConfig(config: IAppRuntimeConfig): void {
  try {
    globalThis.localStorage.setItem(RUNTIME_CONFIG_STORAGE_KEY, JSON.stringify(config));
  } catch {
    console.info('runtime config persist error: IGNORE');
  }
}

applyRuntimeConfig(readStoredRuntimeConfig());

export class Constants {
  public static readStoredRuntimeConfigApply(): IAppRuntimeConfig {
    const storedConfig = readStoredRuntimeConfig();
    applyRuntimeConfig(storedConfig);
    return storedConfig;
  }

  public static setRuntimeConfig(configData: unknown): void {
    const mergedConfig = mergeRuntimeConfig(readStoredRuntimeConfig(), normalizeRuntimeConfig(configData));
    applyRuntimeConfig(mergedConfig);
    persistRuntimeConfig(mergedConfig);
  }

  public static get CLARITY_PROJECT_ID(): string {
    return `${environment.clarity.projectId}`;
  }

  public static get GOOGLE_RECAPTCHA_SITE_ID(): string {
    return environment.recaptchaKey;
  }

  public static get GOOGLE_AUTH_CONFIG(): (typeof DEV_ENVIRONMENT_CONFIG)['googleAuth'] {
    return DEV_ENVIRONMENT_CONFIG.googleAuth;
  }

  public static get MAPBOX_CONFIG(): (typeof DEV_ENVIRONMENT_CONFIG)['mapbox'] {
    return DEV_ENVIRONMENT_CONFIG.mapbox;
  }

  public static get INTERCOM_CONFIG(): (typeof DEV_ENVIRONMENT_CONFIG)['intercom'] {
    return DEV_ENVIRONMENT_CONFIG.intercom;
  }

  public static get BASE_URL(): string {
    return `${environment.baseOwnWorkcoverUrl}/api`;
  }

  public static get WORKCOVER(): string {
    return `${environment.baseApiUrl}/workcover`;
  }

  public static get WELLBEING_URL(): string {
    return `${environment.baseApiUrl}/wellbeing`;
  }

  public static get WORKCOVER_URL(): string {
    return `${environment.baseLogicHeathApiUrl}/workcover`;
  }

  public static get EML_WORKCOVER_URL(): string {
    return `${environment.baseEMLWorkcoverUrl ?? ''}/workcover`;
  }

  public static get TRINITY_WORKCOVER_URL(): string {
    return `${environment.baseTRINITYWorkcoverUrl ?? ''}/workcover`;
  }

  public static get HII_WORKCOVER_URL(): string {
    return `${environment.baseHIIWorkcoverUrl ?? ''}/workcover`;
  }

  public static get BASE_URL_withoutAPI(): string {
    return `${environment.baseOwnWorkcoverUrl}/`;
  }

  public static get BLOG_URL(): string {
    return `${environment.baseBlogUrl}/wp-json/custom-api`;
  }

  public static get BASE_URL_1(): string {
    return `${environment.baseOwnWorkcoverUrl}/`;
  }

  public static get BLOG_CONTENT_API_URL(): string {
    return `${environment.baseBlogUrl}/wp-json/wp/v2/pages?slug=`;
  }

  public static get BLOG_PAGES_API_URL(): string {
    return `${environment.baseBlogUrl}/wp-json/wp/v2/pages`;
  }

  public static get BLOG_POST_API_URL(): string {
    return `${environment.baseBlogUrl}/wp-json/wp/v2/posts`;
  }

  public static get BLOG_USER_API_URL(): string {
    return `${environment.baseBlogUrl}/wp-json/wp/v2/users`;
  }

  public static get BLOG_TAGS_API_URL(): string {
    return `${environment.baseBlogUrl}/wp-json/wp/v2/tags`;
  }

  public static get BLOG_CATEGORIES_API_URL(): string {
    return `${environment.baseBlogUrl}/wp-json/wp/v2/categories`;
  }

  public static get BLOG_POPULAR_POSTS(): string {
    return `${environment.baseBlogUrl}/wp-json/custom-api/posts-ordered`;
  }

  public static get AMENITIES(): string[] {
    const amenities = [
      'Parking',
      'Showers',
      'Swimming Pool',
      'Lockers',
      'Wifi',
      'Dining',
      'Transport Access',
      'Baby Care',
      'Changing Rooms',
      'Toilets'
    ];
    return amenities;
  }

  public static get EMAIL_VALIDATION(): RegExp {
    return /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,3}))|(^.+@[^.].*\.[a-z]{2,})$/;
  }

  public static get MAJOR_CITIES(): string[] {
    const majorCities = ['melbourne', 'brisbane', 'sydney', 'adelaide', 'perth'];
    return majorCities;
  }

  public static get STATE_ACRONYMS(): string[] {
    const stateAcronyms = ['NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA', 'ACT'];
    return stateAcronyms;
  }

  public static get SHOULD_ENCRYPT(): boolean {
    return DEV_ENVIRONMENT_CONFIG.login_encrypt;
  }

  public static get AUTH0_CONFIG(): (typeof DEV_ENVIRONMENT_CONFIG)['auth0'] {
    return DEV_ENVIRONMENT_CONFIG.auth0;
  }

  public static get APP_NAME(): string {
    return environment.appName;
  }

  public static get BRANDING(): typeof environment.branding {
    return environment.branding;
  }

  public static get LOGIN_TYPE(): string {
    return DEV_ENVIRONMENT_CONFIG.login_type;
  }

  public static get earliestAvailabilityOptions(): INameValueOption[] {
    return [
      { name: 'Today', value: 'today' },
      { name: 'Tomorrow', value: 'tomorrow' },
      { name: 'Day after tomorrow', value: 'day_after_tomorrow' }
    ];
  }

  public static get distanceOptions(): INameValueOption[] {
    return [
      { name: 'Any distance', value: '20' },
      { name: 'Under 1km', value: '1' },
      { name: '5km', value: '5' },
      { name: '10km', value: '10' },
      { name: '25km', value: '25' }
    ];
  }

  public static get appointmentTypes(): INameValueOption[] {
    return [
      { name: 'At Venue', value: 'in person' },
      { name: 'Online', value: 'online' },
      { name: 'Mobile', value: 'mobile' }
    ];
  }

  public static get FILTERSLIST(): IFilterListItem[] {
    return [
      {
        type: 'checkbox',
        function: 'earliest_availability',
        heading: 'Earliest availability',
        options: this.earliestAvailabilityOptions,
        value: []
      },
      {
        type: 'radio',
        function: 'distance_from_me',
        heading: 'Distance from me',
        options: this.distanceOptions,
        value: ''
      },
      {
        type: 'radio',
        function: 'gender',
        heading: 'Gender',
        options: [
          { name: 'Female', value: 'female' },
          { name: 'Male', value: 'male' },
          { name: 'Non-binary', value: 'non-binary' }
        ],
        value: ''
      },
      {
        type: 'checkbox',
        function: 'languages_spoken',
        heading: 'Languages spoken',
        options: [],
        value: []
      }
    ];
  }
}
